import { h } from 'preact';
import qrcode from 'qrcode-generator';
import errorLogger from '../../utils/errorLogger';

// Modules of white margin around the symbol. The QR spec asks for 4; 2 is the
// practical floor that phone cameras still read reliably and it buys back real
// estate in a 96px slot inside a modal.
const QUIET_ZONE = 2;

// 0 = pick the smallest symbol version that fits the data.
const AUTO_TYPE_NUMBER = 0;

// 'M' recovers ~15% damage — the usual choice for on-screen codes, where the
// symbol is clean and higher levels would only make the modules smaller.
const ERROR_CORRECTION = 'M';

/**
 * Render a QR code as inline SVG.
 *
 * Colors are hard-coded black-on-white in BOTH themes on purpose: scanners
 * expect dark modules on a light quiet zone, and a theme-aware QR that inverts
 * in dark mode fails to scan on many phones. This is the one place in the app
 * that must not follow the token palette.
 *
 * @param {object} props
 * @param {string} props.value - the text to encode (never a secret; a QR is
 *                 photographable, so callers must pass token-free URLs only)
 * @param {number} [props.size=96] - rendered width/height in CSS pixels
 * @param {string} props.title - accessible label for the image
 */
export default function QrCode({ value, size = 96, title }) {
  if (!value) return null;

  let qr;
  try {
    qr = qrcode(AUTO_TYPE_NUMBER, ERROR_CORRECTION);
    qr.addData(value);
    qr.make();
  } catch (err) {
    // Data too long for any symbol version, or an unexpected encoder failure.
    // The modal still reads correctly without the image, so degrade quietly
    // rather than taking the dialog down.
    errorLogger.logError('QrCodeRenderFailed', err, { length: value.length });
    return null;
  }

  const count = qr.getModuleCount();
  const dimension = count + QUIET_ZONE * 2;

  let path = '';
  for (let row = 0; row < count; row++) {
    for (let col = 0; col < count; col++) {
      if (qr.isDark(row, col)) {
        path += `M${col + QUIET_ZONE} ${row + QUIET_ZONE}h1v1h-1z`;
      }
    }
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${dimension} ${dimension}`}
      shape-rendering="crispEdges"
      role="img"
      aria-label={title}
    >
      <rect width={dimension} height={dimension} fill="#ffffff" />
      <path d={path} fill="#000000" />
    </svg>
  );
}
